// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
    ComboBox,
    IComboBoxOption,
    IComboBoxStyles,
    TextField,
    ITextFieldStyles,
    Spinner,
    SpinnerSize,
} from 'office-ui-fabric-react';
import Styles from './styles.module.scss';

import ErrorMessage from './../../../components/ErrorMessage/ErrorMessage';

const ComboBoxStyle: Partial<IComboBoxStyles> = {
    container: {
        marginTop: 20,
    },
    root: {
        width: 244,
    },
    optionsContainer: {
        maxHeight: 300,
        width: 243,
    },
};


const TextFieldStyle: Partial<ITextFieldStyles> = {
    root: {
        width: 520,
        marginTop: 20,
    },
};

interface RepairOfferProps {
    repairComments?: string;
    repairReason?:IComboBoxOption;
    onParentCommentsChange: (value: string | undefined) => void;
    onParentReasonsChange: (value: IComboBoxOption | undefined) => void;
    selectedReason?: IComboBoxOption;
    readonly?:boolean;
}

export default function DoNotRepairReasonSelection(props: RepairOfferProps, ref: any) {
    const { t } = useTranslation();
    const {repairComments, repairReason} = props;

    const reasonOptions: IComboBoxOption[] = [
        {
            text: t('offer_reason_customer_issues'),
            key: 'offer_reason_customer_issues',
        },
        {
            text: t('offer_reason_device_unrepairable'),
            key: 'offer_reason_device_unrepairable',
        },
        {
            text: t('offer_reason_incorrect_diagnosis'),
            key: 'offer_reason_incorrect_diagnosis',
        },
        {
            text: t('offer_reason_lost_in_transit'),
            key: 'offer_reason_lost_in_transit',
        },
        {
            text: t('offer_reason_received_damaged_fru'),
            key: 'offer_reason_received_damaged_fru',
        },
        {
            text: t('offer_reason_received_incorrect_part'),
            key: 'offer_reason_received_incorrect_part',
        },
        {
            text: t('offer_reason_received_non_functional_fru'),
            key: 'offer_reason_received_non_functional_fru',
        },
        {
            text: t('offer_reason_others'),
            key: 'offer_reason_others',
        },
    ];
    function LoadingSpinner({ loading }: { loading: boolean }) {
        return loading ?
            <Spinner
                className={Styles.spinner}
                size={SpinnerSize.medium}
            /> : null;
    }

    const onCommentsChange = (event: any, value: string | undefined) => {
        props.onParentCommentsChange(value);
    };

    const onReasonChange = (event: any, value: IComboBoxOption | any) => {
        props.onParentReasonsChange(value);
    };
    
    return (
        <div>
            <div className={Styles.comboBox}>
                <ErrorMessage className={Styles.error} />
                <ComboBox
                    ariaLabel={t('reason_type')}
                    dropdownWidth={244}
                    label={t('reason_type')}
                    options={reasonOptions}
                    required={true}
                    selectedKey={
                        (repairReason && repairReason.key) ||
                        undefined
                    }
                    styles={ComboBoxStyle}
                    onChange={onReasonChange}
                    disabled={props.readonly}
                />
            </div>
            <LoadingSpinner loading={false} />
            <TextField
                styles={TextFieldStyle}
                label={t('reason_description')}
                multiline={true}
                rows={5}
                required={true}
                value={repairComments}
                onChange={onCommentsChange}
                placeholder={t('reason_description_placeHolder')}
                disabled={props.readonly}
            />
        </div>
    );
}